import { useEffect, useRef } from 'react';
import { Editor, loader } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import { useCode } from '../../state/code-store';
import { languageForPath, registerLiquidLanguage } from './liquid-language';

// Let @monaco-editor/react use the CDN so we don't ship 10MB of workers.
// This keeps the main bundle ~250KB — Monaco is lazy-loaded on first paint.
loader.config({
  paths: {
    vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.0/min/vs',
  },
});

export function CodeEditor() {
  const activePath = useCode((s) => s.activePath);
  const tab = useCode((s) => (s.activePath ? s.tabs[s.activePath] : null));
  const updateBuffer = useCode((s) => s.updateBuffer);
  const saveActive = useCode((s) => s.saveActive);

  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);

  // Ctrl/Cmd+S anywhere inside the editor pane
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void saveActive();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saveActive]);

  if (!activePath) {
    return (
      <div className="flex h-full items-center justify-center bg-surface-subdued">
        <div className="text-center">
          <svg
            width="56" height="56" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
            className="mx-auto mb-3 text-icon-subdued"
          >
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
          <p className="text-[13px] text-text">Nenhum arquivo aberto</p>
          <p className="mt-1 text-[12px] text-text-subdued">
            Selecione um arquivo na barra lateral para começar.
          </p>
        </div>
      </div>
    );
  }

  if (tab?.loading) {
    return (
      <div className="flex h-full items-center justify-center bg-surface">
        <div className="flex items-center gap-2 text-[13px] text-text-subdued">
          <div className="lc-spin h-4 w-4 rounded-full border-2 border-border border-t-interactive" />
          Carregando {activePath}…
        </div>
      </div>
    );
  }

  if (tab?.error) {
    return (
      <div className="flex h-full items-center justify-center bg-surface">
        <div className="max-w-sm rounded-md border border-critical/30 bg-critical/5 p-4 text-center">
          <p className="text-[13px] font-semibold text-critical">Não foi possível abrir o arquivo</p>
          <p className="mt-1 text-[12px] text-text-subdued">{tab.error}</p>
        </div>
      </div>
    );
  }

  const language = languageForPath(activePath);

  return (
    <div className="h-full w-full bg-white">
      <Editor
        path={activePath}
        language={language}
        theme="lc-light"
        value={tab?.buffer ?? ''}
        onChange={(v) => updateBuffer(activePath, v ?? '')}
        beforeMount={(monaco) => {
          monacoRef.current = monaco;
          registerLiquidLanguage(monaco);
        }}
        onMount={(editor) => {
          editorRef.current = editor;
        }}
        options={{
          fontFamily:
            '"JetBrains Mono", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          fontSize: 13,
          lineHeight: 20,
          fontLigatures: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          smoothScrolling: true,
          renderLineHighlight: 'line',
          padding: { top: 10, bottom: 10 },
          tabSize: 2,
          insertSpaces: true,
          wordWrap: 'on',
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          renderWhitespace: 'selection',
          guides: { indentation: true, bracketPairs: true },
          bracketPairColorization: { enabled: true },
          scrollbar: {
            verticalScrollbarSize: 10,
            horizontalScrollbarSize: 10,
          },
        }}
      />
    </div>
  );
}
